import { FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import { createColumnHelper } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { AccountView } from '../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { FromDateFormField } from './from-date-form-field';
import { OpenBankingAccountDisclaimer } from './open-banking-account-disclaimer';
import { XeroAccountIdFormField } from './xero-account-id-form-field';

const columnHelper = createColumnHelper<AccountView>();

export const accountMappingTableColumnsDefinition = [
  columnHelper.accessor((row) => row.institution?.name, {
    id: 'accountName',
    header: 'Account',
    cell: ({ row }) => {
      const name = `${row.original.institution?.name} (${row.original.currency})`;
      const formattedBalance = getAmountWithCurrency({
        amount: row.original.balance,
        currency: row.original.currency,
        isAccountBalance: true,
      });
      const description = `Balance ${formattedBalance}`;

      return (
        <AvatarNameDescription
          avatar={{ showAvatar: true, avatarUrl: row.original.institution?.logo, size: 'xs' }}
          name={name}
          description={description}
        />
      );
    },
    sortingFn: (rowA, rowB) => {
      const isModulrA = rowA.original.fspAdapter === FinancialServiceProviderAdapters.MODULR;
      const isModulrB = rowB.original.fspAdapter === FinancialServiceProviderAdapters.MODULR;

      if (isModulrA && !isModulrB) {
        return -1;
      }

      if (!isModulrA && isModulrB) {
        return 1;
      }

      return 0;
    },
    size: 30,
  }),
  columnHelper.display({
    id: 'rightArrow',
    cell: () => (
      <div className="h-[30px] w-[30px] rounded-full bg-white p-1.5">
        <ChevronRight className="h-full w-full text-seapoint-gray-text" />
      </div>
    ),
    size: 5,
    minSize: 5,
  }),
  columnHelper.display({
    id: 'xeroAccountName',
    header: 'Xero Account',
    cell: ({ row, table }) => {
      const allXeroBankAccounts = table.options.meta?.xeroBankAccounts ?? [];

      const matchingCurrencyExistingXeroBankAccounts = allXeroBankAccounts.filter(
        (a) => a.type === 'existing' && a.currency === row.original.currency,
      );
      const xeroCreateAccountOptions = allXeroBankAccounts.filter((a) => a.type === 'create');

      return (
        <XeroAccountIdFormField
          row={row}
          xeroBankAccounts={[...matchingCurrencyExistingXeroBankAccounts, ...xeroCreateAccountOptions]}
          isCreatingXeroAccount={table.options.meta?.isPrimaryActionLoading ?? false}
          onCreateXeroAccount={async () => {
            await table.options.meta?.onRowPrimaryAction?.(row.original.id);
          }}
        />
      );
    },
    size: 40,
  }),
  columnHelper.display({
    id: 'importDate',
    header: 'Import transactions from',
    cell: ({ row }) => {
      if (row.original.fspAdapter !== FinancialServiceProviderAdapters.MODULR) {
        // NOTE: this should never run because we are filtering OB accounts out from the list of accounts now
        // Leaving it for now in case that filter is reverted after we launch it
        return <OpenBankingAccountDisclaimer />;
      }

      return <FromDateFormField row={row} />;
    },
    size: 25,
  }),
];
